import * as THREE from 'three';
import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import type { AnimatedProp } from './coast-props.js';
import { terrainHeight } from '../../../shared/game/terrain.js';
import { withFogOfWar } from './fog-of-war.js';
import { animateTransform } from '../scene-transforms.js';

/** Height of the sea sheet; every water surface on the coast lies at this level. */
export const WATER_LEVEL = .09;

/** Texel size of the shore field, in metres. */
const CELL = 1.5;
/** The near channel spans this many metres of signed distance: land side, then water side. */
const NEAR_LAND = 8, NEAR_WATER = 24;
/** Open water reaches full depth this far from land. */
const DEEP = 140;
/** Water cut into the land lies deep between cliffs, so it starts part of the way down. */
const INLAND_DEPTH = .5;
/** The most water contacts (hulls, posts, wrecks) one frame shades. */
const MAX_CONTACTS = 32;

/**
 * Where the water is: a signed distance to the shoreline (positive offshore) and a depth, sampled over the whole map from the
 * same tide line and cove outlines the water meshes are built from. The sea shader reads it for colour, transparency and surf;
 * the ground reads it for the wet strand, the swash and the seabed.
 */
export interface ShoreField {
  texture: THREE.DataTexture;
  origin: THREE.Vector2; size: THREE.Vector2;
  /** Signed distance in metres from the shoreline, positive over water; clamped to the field's near range. */
  distance(x: number, z: number): number;
  /** Points along the waterline where the land rises steeply out of the sea, with the direction out to sea. */
  cliffs: { x: number; z: number; nx: number; nz: number }[];
}

export function createShoreField(map: MapDefinition, tide: { z: number; edge: number }[], coves: Point[][]): ShoreField {
  const columns = Math.ceil(map.width / CELL) + 1, rows = Math.ceil(map.depth / CELL) + 1, count = columns * rows;
  const origin = new THREE.Vector2(-map.width / 2 - CELL / 2, -map.depth / 2 - CELL / 2), size = new THREE.Vector2(columns * CELL, rows * CELL);
  const line = [...tide].sort((a, b) => a.z - b.z);
  // The sea sheet runs straight between tide-line rows, so the field does too.
  const edgeAt = (z: number) => {
    if (!line.length || z < line[0].z || z > line.at(-1)!.z) return -Infinity;
    const i = Math.max(1, line.findIndex(row => row.z >= z)), a = line[i - 1], b = line[i];
    return b.z === a.z ? a.edge : a.edge + (b.edge - a.edge) * (z - a.z) / (b.z - a.z);
  };
  const boxes = coves.map(outline => ({ outline, x0: Math.min(...outline.map(p => p.x)), x1: Math.max(...outline.map(p => p.x)), z0: Math.min(...outline.map(p => p.z)), z1: Math.max(...outline.map(p => p.z)) }));
  const inside = (outline: Point[], x: number, z: number) => {
    let hit = false;
    for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
      const a = outline[i], b = outline[j];
      if ((a.z > z) !== (b.z > z) && x < (b.x - a.x) * (z - a.z) / (b.z - a.z) + a.x) hit = !hit;
    }
    return hit;
  };
  const water = new Uint8Array(count), inland = new Uint8Array(count);
  for (let j = 0; j < rows; j++) {
    const z = origin.y + (j + .5) * CELL, edge = edgeAt(z);
    for (let i = 0; i < columns; i++) {
      const x = origin.x + (i + .5) * CELL, k = j * columns + i;
      // Only where the ground is below the surface: rock or sand standing out of the sheet is shore, so the surf follows it.
      // A cell short of the sheet's edge, so the surface always ends where the shallows have faded out.
      if (x < edge - CELL) water[k] = 1;
      else if (boxes.some(b => x >= b.x0 && x <= b.x1 && z >= b.z0 && z <= b.z1 && inside(b.outline, x, z))) water[k] = inland[k] = 1;
      if (water[k] && map.terrain && terrainHeight(map, x, z) > WATER_LEVEL) water[k] = inland[k] = 0;
    }
  }
  const toLand = nearestDistance(water, columns, rows, 0), toWater = nearestDistance(water, columns, rows, 1);
  const signed = new Float32Array(count), data = new Uint8Array(count * 4);
  for (let k = 0; k < count; k++) {
    const s = water[k] ? toLand[k] * CELL - CELL / 2 : -(toWater[k] * CELL - CELL / 2);
    signed[k] = s;
    const near = THREE.MathUtils.clamp((s + NEAR_LAND) / (NEAR_LAND + NEAR_WATER), 0, 1);
    const depth = water[k] ? Math.max(inland[k] ? INLAND_DEPTH : 0, THREE.MathUtils.clamp(s / DEEP, 0, 1)) : 0;
    data[k * 4] = Math.round(near * 255); data[k * 4 + 1] = Math.round(depth * 255); data[k * 4 + 2] = inland[k] * 255; data[k * 4 + 3] = 255;
  }
  const texture = new THREE.DataTexture(data, columns, rows, THREE.RGBAFormat);
  texture.magFilter = texture.minFilter = THREE.LinearFilter; texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping; texture.needsUpdate = true;
  const distance = (x: number, z: number) => {
    const i = THREE.MathUtils.clamp(Math.floor((x - origin.x) / CELL), 0, columns - 1), j = THREE.MathUtils.clamp(Math.floor((z - origin.y) / CELL), 0, rows - 1);
    return THREE.MathUtils.clamp(signed[j * columns + i], -NEAR_LAND, NEAR_WATER);
  };
  // Cliff feet: shore cells whose ground climbs well above the sea a couple of metres inland, one every few metres.
  const cliffs: ShoreField['cliffs'] = [], taken = new Set<string>();
  if (map.terrain) for (let j = 1; j < rows - 1; j++) for (let i = 1; i < columns - 1; i++) {
    const k = j * columns + i;
    if (water[k]) continue;
    let nx = 0, nz = 0;
    for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (water[k + dj * columns + di]) { nx += di; nz += dj; }
    const length = Math.hypot(nx, nz);
    if (!length) continue;
    nx /= length; nz /= length;
    const x = origin.x + (i + .5) * CELL, z = origin.y + (j + .5) * CELL, slot = `${Math.round(x / 3)},${Math.round(z / 3)}`;
    if (taken.has(slot) || terrainHeight(map, x - nx * 2.5, z - nz * 2.5) < 1.3) continue;
    taken.add(slot); cliffs.push({ x, z, nx, nz });
  }
  return { texture, origin, size, distance, cliffs };
}

/** Cells to the nearest cell of the other kind, propagating each cell's nearest seed in two sweeps (close to Euclidean). */
function nearestDistance(water: Uint8Array, columns: number, rows: number, seedKind: number) {
  const far = 1e6, sx = new Int32Array(water.length).fill(-far), sz = new Int32Array(water.length).fill(-far), out = new Float32Array(water.length);
  for (let k = 0; k < water.length; k++) if (water[k] === seedKind) { sx[k] = k % columns; sz[k] = Math.floor(k / columns); }
  const relax = (i: number, j: number, di: number, dj: number) => {
    const ni = i + di, nj = j + dj;
    if (ni < 0 || nj < 0 || ni >= columns || nj >= rows) return;
    const k = j * columns + i, n = nj * columns + ni;
    if (sx[n] === -far) return;
    const current = (sx[k] - i) ** 2 + (sz[k] - j) ** 2, offered = (sx[n] - i) ** 2 + (sz[n] - j) ** 2;
    if (sx[k] === -far || offered < current) { sx[k] = sx[n]; sz[k] = sz[n]; }
  };
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < columns; i++) { relax(i, j, -1, 0); relax(i, j, -1, -1); relax(i, j, 0, -1); relax(i, j, 1, -1); }
    for (let i = columns - 1; i >= 0; i--) relax(i, j, 1, 0);
  }
  for (let j = rows - 1; j >= 0; j--) {
    for (let i = columns - 1; i >= 0; i--) { relax(i, j, 1, 0); relax(i, j, 1, 1); relax(i, j, 0, 1); relax(i, j, -1, 1); }
    for (let i = 0; i < columns; i++) relax(i, j, -1, 0);
  }
  for (let k = 0; k < water.length; k++) out[k] = sx[k] === -far ? 1e4 : Math.hypot(sx[k] - k % columns, sz[k] - Math.floor(k / columns));
  return out;
}

/** Where breakers hit rock: a cliff foot on the shore or a stack standing in the sea. */
export interface SprayEmitter { x: number; z: number; nx: number; nz: number; strength: number }

const PER_EMITTER = 14;

/**
 * Spray thrown up where each breaker meets rock, timed to the same surf the sea and the strand draw: a burst of droplets that
 * rises a metre or three, drifts out to sea and falls back. Lit by the sky and the sun, so it dims at night.
 */
export function createSpray(scene: THREE.Scene, emitters: SprayEmitter[]): AnimatedProp | undefined {
  if (!emitters.length) return undefined;
  let seed = 4421;
  const random = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  const count = emitters.length * PER_EMITTER, base = new Float32Array(count * 3), motion = new Float32Array(count * 4), timing = new Float32Array(count * 4);
  emitters.forEach((emitter, e) => {
    // The same wobble as surfPhase at the waterline, so the burst lands with the breaker.
    const wave = Math.sin(emitter.z * .045 + Math.sin(emitter.x * .03)) * 2.2 + Math.sin(emitter.z * .13 - emitter.x * .07) * .6;
    for (let p = 0; p < PER_EMITTER; p++) {
      const k = e * PER_EMITTER + p, spread = (random() - .5) * 1.8, out = .2 + random() * 1.1;
      const dx = emitter.nx * Math.cos(spread) - emitter.nz * Math.sin(spread), dz = emitter.nx * Math.sin(spread) + emitter.nz * Math.cos(spread);
      base.set([emitter.x + emitter.nx * .6 + (random() - .5) * 2.4 * Math.abs(emitter.nz), WATER_LEVEL + .1, emitter.z + emitter.nz * .6 + (random() - .5) * 2.4 * Math.abs(emitter.nx)], k * 3);
      motion.set([dx * out, dz * out, (2.2 + random() * 3.6) * emitter.strength, .3 + random() * .6], k * 4);
      timing.set([wave, random() * .3, 1.1 + random() * .8, random()], k * 4);
    }
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(base, 3)); geometry.setAttribute('motion', new THREE.BufferAttribute(motion, 4)); geometry.setAttribute('timing', new THREE.BufferAttribute(timing, 4));
  const light = { value: new THREE.Color(.5, .5, .5) }, pointScale = { value: 500 };
  const material = withFogOfWar(new THREE.ShaderMaterial({
    uniforms: { waterTime: shared.waterTime, sprayLight: light, pointScale }, transparent: true, depthWrite: false,
    vertexShader: `attribute vec4 motion, timing; uniform float waterTime, pointScale; varying float vLife;
      void main() {
        float cycles = (waterTime * 1.1 + timing.x - 1.5708) / 6.2832, t = fract(cycles) * 5.712 - timing.y;
        // Every breaker throws a different height.
        float strength = .45 + .55 * fract(sin(floor(cycles) * 12.9898 + timing.w * 78.233) * 43758.5453);
        vLife = t / timing.z;
        vec3 p = position + vec3(motion.x, 0., motion.y) * t * (.6 + strength * .6) + vec3(0., motion.z * strength * t - 4.9 * t * t, 0.);
        p.y = max(p.y, position.y);
        vec4 view = modelViewMatrix * vec4(p, 1.);
        float perspective = projectionMatrix[2][3] < -.5 ? 1. / -view.z : 1.;
        float alive = step(0., t) * step(vLife, 1.);
        gl_PointSize = alive * motion.w * (1. + vLife * 1.8) * pointScale * projectionMatrix[1][1] * perspective;
        gl_Position = projectionMatrix * view;
      }`,
    fragmentShader: `uniform vec3 sprayLight; varying float vLife;
      void main() {
        float r = length(gl_PointCoord - .5);
        // A soft puff, torn at the edge so droplets read as spray rather than discs.
        float torn = fract(sin(dot(floor(gl_PointCoord * 6.), vec2(12.9898, 78.233))) * 43758.5453);
        float alpha = exp(-r * r * 14.) * (.6 + .4 * torn) * pow(1. - clamp(vLife, 0., 1.), 1.4) * .42;
        if (alpha < .01) discard;
        gl_FragColor = vec4(sprayLight, alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  }));
  const points = new THREE.Points(geometry, material); scene.add(points);
  let hemisphere: THREE.HemisphereLight | undefined, sun: THREE.DirectionalLight | undefined;
  const tint = new THREE.Color();
  return { update: () => {
    hemisphere ??= scene.children.find(child => (child as THREE.HemisphereLight).isHemisphereLight) as THREE.HemisphereLight | undefined;
    sun ??= scene.children.find(child => (child as THREE.DirectionalLight).isDirectionalLight && child.castShadow) as THREE.DirectionalLight | undefined;
    light.value.setRGB(.06, .07, .08);
    if (hemisphere) light.value.add(tint.copy(hemisphere.color).multiplyScalar(hemisphere.intensity * .32));
    if (sun) light.value.add(tint.copy(sun.color).multiplyScalar(sun.intensity * .16));
    pointScale.value = typeof window === 'undefined' ? 500 : window.innerHeight * Math.min(2, window.devicePixelRatio) / 2;
  } };
}

/**
 * A tileable ripple field: many small waves on whole-number wave vectors, so it repeats exactly. RG hold the slope, B the height
 * (foam breaks up along it). Wave vectors lean along the wind so the chop has a grain rather than looking like noise.
 */
function rippleTexture(size = 256) {
  let seed = 9173;
  const random = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  const waves = Array.from({ length: 56 }, () => {
    const scale = 2 + Math.floor(random() ** 1.7 * 22), angle = .5 + (random() - .5) * 2.4;
    const kx = Math.round(Math.cos(angle) * scale), ky = Math.round(Math.sin(angle) * scale);
    return { kx: kx || 1, ky, amplitude: Math.hypot(kx || 1, ky) ** -1.5, phase: random() * Math.PI * 2 };
  });
  const total = waves.reduce((sum, wave) => sum + wave.amplitude, 0);
  const slopes = new Float32Array(size * size * 2), heights = new Float32Array(size * size);
  let steepest = 0;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    let h = 0, dx = 0, dy = 0;
    for (const { kx, ky, amplitude, phase } of waves) {
      const a = Math.PI * 2 * (kx * x + ky * y) / size + phase;
      h += Math.sin(a) * amplitude; dx += Math.cos(a) * amplitude * kx; dy += Math.cos(a) * amplitude * ky;
    }
    const k = y * size + x; heights[k] = h / total; slopes[k * 2] = dx; slopes[k * 2 + 1] = dy;
    steepest = Math.max(steepest, Math.abs(dx), Math.abs(dy));
  }
  const data = new Uint8Array(size * size * 4);
  for (let k = 0; k < size * size; k++) {
    data[k * 4] = Math.round((slopes[k * 2] / steepest * .5 + .5) * 255); data[k * 4 + 1] = Math.round((slopes[k * 2 + 1] / steepest * .5 + .5) * 255);
    data[k * 4 + 2] = Math.round(THREE.MathUtils.clamp(heights[k] * .5 + .5, 0, 1) * 255); data[k * 4 + 3] = 255;
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping; texture.magFilter = THREE.LinearFilter; texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true; texture.anisotropy = 4; texture.needsUpdate = true;
  return texture;
}

/** Hulls, posts and wrecks standing in the water: each makes a ring of foam where it meets the surface and sends out ripples. */
export interface WaterContact { x: number; z: number; angle: number; halfX: number; halfZ: number; rock?: boolean }

const shared = {
  waterTime: { value: 0 }, waterRipples: { value: null as THREE.Texture | null }, shoreField: { value: null as THREE.Texture | null },
  fieldOrigin: { value: new THREE.Vector2() }, fieldSize: { value: new THREE.Vector2(1, 1) }, mapHalf: { value: new THREE.Vector2(1, 1) },
  contactAt: { value: Array.from({ length: MAX_CONTACTS }, () => new THREE.Vector4()) },
  contactSize: { value: Array.from({ length: MAX_CONTACTS }, () => new THREE.Vector2()) }, contactCount: { value: 0 },
};

const FIELD = `uniform sampler2D shoreField, waterRipples; uniform vec2 fieldOrigin, fieldSize, mapHalf; uniform float waterTime;
vec3 shoreAt(vec2 p) {
  vec4 f = texture2D(shoreField, (p - fieldOrigin) / fieldSize);
  // Beyond the map the field holds its edge: the open sea past the west and south edges keeps getting deeper.
  float outside = max(abs(p.x) - mapHalf.x, abs(p.y) - mapHalf.y);
  float s = f.r * ${(NEAR_LAND + NEAR_WATER).toFixed(1)} - ${NEAR_LAND.toFixed(1)};
  return vec3(s, max(f.g, clamp(outside / ${DEEP.toFixed(1)}, 0., 1.) * step(0., s)), f.b);
}
// The surf's phase at a point: one set of breakers every ~5.7 s, rolling in along the shore with a slow wobble so the fronts
// do not march in step from one end of the coast to the other.
float surfPhase(vec2 p, float s) { return s * .55 + waterTime * 1.1 + sin(p.y * .045 + sin(p.x * .03)) * 2.2 + sin(p.y * .13 - p.x * .07) * .6; }
`;

/** The sea, the coves and the open water beyond the map share this material. */
export function createWaterMaterial(map: MapDefinition, field: ShoreField) {
  shared.waterRipples.value ??= rippleTexture();
  shared.shoreField.value = field.texture; shared.fieldOrigin.value.copy(field.origin); shared.fieldSize.value.copy(field.size); shared.mapHalf.value.set(map.width / 2, map.depth / 2);
  // One pass for both faces: a split transparent double-sided draw re-selects this large shader twice per mesh every frame.
  const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: .13, metalness: 0, transparent: true, side: THREE.DoubleSide, forceSinglePass: true });
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, shared);
    shader.vertexShader = 'varying vec3 vWaterAt;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vWaterAt = (modelMatrix * vec4(transformed, 1.)).xyz;');
    shader.fragmentShader = `varying vec3 vWaterAt; uniform vec4 contactAt[${MAX_CONTACTS}]; uniform vec2 contactSize[${MAX_CONTACTS}]; uniform int contactCount;
      ${FIELD}
      ` + shader.fragmentShader
      .replace('#include <color_fragment>', `#include <color_fragment>
        {
        vec2 at = vWaterAt.xz;
        vec3 shore = shoreAt(at);
        float s = shore.x, depth = shore.y;
        // Two layers of chop drifting across each other, at scales that never line up, plus long swells.
        vec4 chopA = texture2D(waterRipples, at / 23. + waterTime * vec2(.011, .006));
        vec4 chopB = texture2D(waterRipples, mat2(.8, -.6, .6, .8) * at / 8.7 + waterTime * vec2(-.017, .021));
        vec4 chopC = texture2D(waterRipples, mat2(.6, .8, -.8, .6) * at / 2.3 + waterTime * vec2(.04, -.03));
        vec2 slope = (chopA.rg - .5) * .3 + (chopB.rg - .5) * .22 + (chopC.rg - .5) * .14;
        slope += vec2(.6, .45) * cos(dot(at, vec2(.15, .11)) + waterTime * .9) * .1 + vec2(-.3, .7) * cos(dot(at, vec2(-.07, .2)) - waterTime * .7) * .07;
        float lace = chopC.b * .55 + chopB.b * .3 + chopA.b * .15;
        // Breakers: crests run in parallel to the shore, steepen and turn to foam as the water shoals, and leave streaks behind.
        float foam = 0.;
        if (s < 16.) {
          float eps = 1.5, phase = surfPhase(at, s);
          vec2 toShore = vec2(shoreAt(at + vec2(eps, 0.)).x - s, shoreAt(at + vec2(0., eps)).x - s) / eps;
          float shoal = smoothstep(16., 2.5, s), crest = pow(.5 + .5 * sin(phase), 16.);
          slope += toShore * cos(phase) * shoal * .55;
          foam += crest * shoal * shoal * smoothstep(.5, .58, lace + crest * .18 + shoal * .08);
          foam += pow(.5 + .5 * sin(phase + .9), 4.) * shoal * smoothstep(.6, .66, lace) * .6;
          // At the waterline the foam surges in with each breaker, then thins and drifts until the next.
          float cycle = fract((surfPhase(at, 0.) - 1.57) / 6.2832), fresh = 1. - smoothstep(0., .75, cycle);
          float drift = texture2D(waterRipples, at / 1.9 + waterTime * vec2(.06, -.045)).b;
          foam += smoothstep(.4 + fresh * 1.6, 0., s) * smoothstep(.62 - fresh * .22, .7 - fresh * .22, drift * .6 + lace * .4 + smoothstep(.5, 0., s) * .15);
        }
        // Hulls and posts: a foam collar at the waterline and rings spreading from it.
        for (int i = 0; i < ${MAX_CONTACTS}; i++) {
          if (i >= contactCount) break;
          vec4 c = contactAt[i]; vec2 extent = contactSize[i], d = at - c.xy;
          float reach = max(extent.x, extent.y) + 5.;
          if (dot(d, d) > reach * reach) continue;
          vec2 local = vec2(d.x * c.z - d.y * c.w, d.x * c.w + d.y * c.z);
          float r = length(local / extent), gap = (r - 1.) * min(extent.x, extent.y);
          if (gap < -.2) continue;
          vec2 outward = normalize(d + 1e-4);
          float fade = exp(-max(gap, 0.) * .55);
          slope += outward * cos(gap * 5. - waterTime * 3.2 + c.x) * fade * .22;
          foam = max(foam, smoothstep(.45, 0., gap) * smoothstep(.4, .48, lace + smoothstep(.2, 0., gap) * .3));
        }
        foam = clamp(foam, 0., 1.);
        waterFoam = foam;
        slope *= 1. - foam * .7;
        waterNormal = normalize(vec3(-slope.x, 1., -slope.y));
        // Clear over the shelf, so the wet sand shows through; olive in the shallows, slate further out, near-black in the deep.
        vec3 shallow = vec3(.09, .16, .13), middle = vec3(.025, .08, .085), deep = vec3(.008, .028, .042);
        vec3 body = mix(shallow, middle, smoothstep(0., .12, depth));
        body = mix(body, deep, smoothstep(.1, .7, depth));
        diffuseColor.rgb = mix(body, vec3(.82, .8, .74), foam);
        diffuseColor.a = max(clamp(1. - exp(-max(s, 0.) * .32), 0., 1.), foam * .95);
        if (shore.z > .5) diffuseColor.a = max(diffuseColor.a, smoothstep(0., 1.5, s));
        }
      `)
      .replace('void main() {', 'void main() {\n  vec3 waterNormal = vec3(0., 1., 0.); float waterFoam = 0.;')
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\n  roughnessFactor = mix(roughnessFactor, 1., waterFoam);')
      .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n  normal = normalize((viewMatrix * vec4(waterNormal, 0.)).xyz);')
      .replace('#include <opaque_fragment>', `
        // A sky that is not rendered can still be reflected: the hemisphere's sky colour, the haze towards the horizon and soft
        // cloud shapes, more of it at grazing angles.
        vec3 sky = vec3(.35, .42, .5);
        #if NUM_HEMI_LIGHTS > 0
          sky = hemisphereLights[0].skyColor * .2;
        #endif
        #ifdef USE_FOG
          sky = mix(sky, fogColor, .35);
        #endif
        vec2 cloudAt = vWaterAt.xz * .006 + waterNormal.xz * .9 + waterTime * vec2(.002, .001);
        float cloud = sin(cloudAt.x * 6.3 + sin(cloudAt.y * 4.1)) * sin(cloudAt.y * 5.2 - sin(cloudAt.x * 3.7)) * .5 + .5;
        sky *= .85 + cloud * .25;
        float facing = clamp(dot(normal, normalize(vViewPosition)), 0., 1.);
        float fresnel = (.02 + .98 * pow(1. - facing, 5.)) * 1.3;
        outgoingLight = mix(outgoingLight, sky + outgoingLight * .5, clamp(fresnel, 0., .5) * (1. - waterFoam));
        diffuseColor.a = max(diffuseColor.a, clamp(fresnel, 0., 1.) * .35);
        #include <opaque_fragment>`);
  };
  material.customProgramCacheKey = () => 'coast-water';
  return material;
}

/**
 * The strand and the seabed: sand darkens where the swash last reached, a thin film of water runs up the beach behind each
 * breaker and sinks away with a lace of foam at its edge, and the shelf under clear water carries sun-caustics.
 */
export function patchWaterGround(material: THREE.MeshStandardMaterial) {
  const compile = material.onBeforeCompile, key = material.customProgramCacheKey;
  material.onBeforeCompile = (shader, renderer) => {
    compile.call(material, shader, renderer);
    Object.assign(shader.uniforms, shared);
    shader.vertexShader = 'varying vec3 vShoreAt;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vShoreAt = (modelMatrix * vec4(transformed, 1.)).xyz;');
    shader.fragmentShader = `varying vec3 vShoreAt;\n${FIELD}\n` + shader.fragmentShader
      .replace('void main() {', 'void main() {\n  float shoreFilm = 0.;')
      .replace('#include <color_fragment>', `#include <color_fragment>
        {
          vec2 at = vShoreAt.xz;
          vec3 shore = shoreAt(at);
          float s = shore.x, low = 1. - smoothstep(.9, 2.4, vShoreAt.y);
          if (s > -7. && low > 0.) {
            float lace = texture2D(waterRipples, at / 6.1).b;
            if (s < 0.) {
              // The swash: water runs up after each breaker lands, then drains back down the slope.
              float cycle = fract((surfPhase(at, 0.) - 1.57) / 6.2832);
              float reach = (1.8 + 1.6 * (.5 + .5 * sin(at.y * .07 + at.x * .02))) * pow(sin(3.1416 * min(cycle * 1.6, 1.)), .6);
              float land = -s;
              float film = smoothstep(reach + .15, reach - .5, land) * step(cycle, .62) * low;
              float edge = smoothstep(.45, 0., abs(land - reach)) * step(cycle, .62) * smoothstep(.35, .7, lace) * low;
              float wet = smoothstep(4.6, .4, land + (lace - .5) * .8) * low;
              diffuseColor.rgb *= 1. - wet * .32 - film * .18;
              diffuseColor.rgb = mix(diffuseColor.rgb, vec3(.8, .78, .72), edge * .7);
              shoreFilm = max(film * .8, wet * .25);
            } else {
              // Seabed: dark wet sand under a few hand-widths of water, with caustics from the chop overhead.
              diffuseColor.rgb *= .62;
              vec4 a = texture2D(waterRipples, at / 3.7 + waterTime * vec2(.03, .012)), b = texture2D(waterRipples, at / 2.9 - waterTime * vec2(.02, .028));
              float caustic = pow(1. - abs(a.b + b.b - 1.), 6.);
              diffuseColor.rgb *= 1. + caustic * .9 * smoothstep(10., 1., s);
            }
          }
          // Rock rising out of the sea: dark and wet to above the surface, washed in foam to the height each breaker climbs.
          if (s > -3. && s < 1.5 && vShoreAt.y < ${(WATER_LEVEL + 2).toFixed(2)}) {
            float steep = 1. - smoothstep(.55, .8, dot(normalize(vNormal), normalize((viewMatrix * vec4(0., 1., 0., 0.)).xyz)));
            if (steep > 0.) {
              float lace = texture2D(waterRipples, vec2(at.x + at.y, vShoreAt.y * 3.) / 4.3 + waterTime * vec2(.05, -.08)).b;
              float cycle = fract((surfPhase(at, 0.) - 1.57) / 6.2832);
              float surge = pow(sin(3.1416 * min(cycle * 2.4, 1.)), .7) * step(cycle, .42);
              float top = ${(WATER_LEVEL + .3).toFixed(2)} + surge * 1.15 + (lace - .5) * .3;
              float wash = smoothstep(top + .06, top - .12, vShoreAt.y) * steep;
              float wetRock = smoothstep(${(WATER_LEVEL + 1.5).toFixed(2)}, ${(WATER_LEVEL + .3).toFixed(2)}, vShoreAt.y) * steep;
              diffuseColor.rgb *= 1. - wetRock * .4;
              diffuseColor.rgb = mix(diffuseColor.rgb, vec3(.8, .79, .75), wash * smoothstep(.3, .55, lace + surge * .2) * .85);
              shoreFilm = max(shoreFilm, max(wetRock * .55, wash));
            }
          }
        }`)
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\n  roughnessFactor = mix(roughnessFactor, .28, shoreFilm);');
  };
  material.customProgramCacheKey = () => key.call(material) + '|water-shore';
}

/** Feeds the shaders the time and the contacts on screen (`inView`), since only so many fit. */
export function updateWater(time: number, contacts: WaterContact[], inView?: (x: number, z: number, radius: number) => boolean) {
  shared.waterTime.value = time;
  let count = 0;
  for (const c of contacts) {
    if (count === MAX_CONTACTS) break;
    if (inView && !inView(c.x, c.z, Math.max(c.halfX, c.halfZ) + 6)) continue;
    shared.contactAt.value[count].set(c.x, c.z, Math.cos(c.angle), Math.sin(c.angle)); shared.contactSize.value[count++].set(c.halfX, c.halfZ);
  }
  shared.contactCount.value = count;
}

/** What a prop declares in `userData.waterContact`: its half extents at the waterline, how far up it sits in the water, and whether it rides the swell. */
interface ContactSpec { halfX: number; halfZ: number; waterline?: number; floats?: boolean }

/**
 * Seats props that stand in water: anything marked with a water contact gets a foam collar and ripples, a hull is lowered to its
 * waterline, and a floating one rides the swell. Props on dry land are left alone, so a beached boat stays on the sand.
 */
export function settleInWater(roots: THREE.Object3D[], field: ShoreField, contacts: WaterContact[]): AnimatedProp | undefined {
  const riders: { object: THREE.Object3D; y: number; phase: number }[] = [], at = new THREE.Vector3(), scale = new THREE.Vector3(), turn = new THREE.Quaternion(), euler = new THREE.Euler();
  for (const root of roots) {
    root.updateMatrixWorld(true);
    root.traverse(object => {
      const spec = object.userData.waterContact as ContactSpec | undefined;
      if (!spec) return;
      object.getWorldPosition(at); object.getWorldScale(scale); object.getWorldQuaternion(turn);
      const offshore = field.distance(at.x, at.z);
      if (offshore < -.5) return;
      contacts.push({ x: at.x, z: at.z, angle: euler.setFromQuaternion(turn, 'YXZ').y, halfX: spec.halfX * scale.x, halfZ: spec.halfZ * scale.z });
      if (object !== root || offshore < .5) return;
      if (spec.waterline !== undefined) root.position.y = WATER_LEVEL - spec.waterline * scale.y;
      if (spec.floats) riders.push({ object: animateTransform(root), y: root.position.y, phase: (at.x * 1.7 + at.z * .9) % (Math.PI * 2) });
    });
  }
  if (!riders.length) return undefined;
  return { update: time => {
    for (const { object, y, phase } of riders) {
      object.position.y = y + Math.sin(time * .9 + phase) * .05;
      object.rotation.x = Math.sin(time * .55 + phase * 2) * .025; object.rotation.z = Math.sin(time * .7 + phase) * .04;
    }
  } };
}
